import React, { Dispatch, SetStateAction, useEffect, useRef } from 'react';
import { Text } from '@fiverr-private/typography';
import { useIntersectionObserver } from '@fiverr-private/hooks';
import { I18n } from '@fiverr-private/i18n-react';
import { Container } from '@fiverr-private/layout_components';
import { IconButton } from '@fiverr-private/button';
import { PencilIcon } from '@fiverr-private/icons';
import { colorGrey900 } from '@fiverr-private/sass/helpers';
import { WorkExperience } from '../../../gql-operations/gql-generated';
import { useMonitoring } from '../../../utils/monitoring';
import { FederatedSeller } from '../../../types';
import { useSellerData } from '../../../hooks/useSellerData';
import { useIsSelfViewFreelancer } from '../../../hooks/useIsSelfViewFreelancer';
import { employmentTypeEnumToTranslationKey, formatDateRange, shouldDisplayEmploymentType, T_KEY } from '../utils';
import workExperienceBIEvents from '../utils/biEvents';
import { CurrentViewState, WorkExperienceViewStates } from '../shared/types';
import { capitalize, iconContainer } from '../shared/styles.ve.css';

import ExperienceDescription from './ExperienceDescription';

interface ExperienceDetailsProps {
    experience: WorkExperience;
    isCard?: boolean;
    inTimeline?: boolean;
    index: number;
    setCurrentViewState?: Dispatch<SetStateAction<CurrentViewState>>;
}

const ExperienceDetails = ({
    experience,
    isCard = false,
    inTimeline,
    index,
    setCurrentViewState,
}: ExperienceDetailsProps) => {
    const isSelfView = useIsSelfViewFreelancer();

    const { Providers, fireImpressionEvents } = useMonitoring();
    const { sellerId } = useSellerData(selectSellerSlice);
    const observableRef = useRef<HTMLElement>(null);
    const entry = useIntersectionObserver(observableRef, { threshold: 1, once: true });
    const isVisible = Boolean(entry?.isIntersecting);

    const onEditExperienceClick = () => {
        setCurrentViewState?.((state) => ({
            ...state,
            viewState: WorkExperienceViewStates.AddEdit,
            experienceId: experience.id,
        }));
    };

    useEffect(() => {
        if (isVisible && !isCard) {
            fireImpressionEvents({
                [Providers.BigQuery]: workExperienceBIEvents.impressionOnSingleExperience({
                    sellerId,
                    companyId: experience.company.id,
                    companyName: experience.company.name,
                    startDate: experience.startDate,
                    endDate: experience.endDate,
                    experiencePosition: index,
                    workExperiences: [experience],
                }),
            });
        }
    }, [isVisible, Providers.Mixpanel, Providers.BigQuery, fireImpressionEvents, experience, sellerId, index, isCard]);

    return (
        <Container
            display="flex"
            direction="column"
            color="grey_1100"
            key={experience.id}
            ref={observableRef}
            data-testid="experience-details"
        >
            <Container>
                <Container display="flex" justifyContent="spaceBetween">
                    <Text fontWeight="semibold" className={capitalize}>
                        {experience.title}
                    </Text>
                    {isSelfView && isCard && (
                        <IconButton
                            size="xs"
                            variant="ghost"
                            aria-label="edit-work-experience"
                            className={iconContainer}
                            onClick={onEditExperienceClick}
                        >
                            <PencilIcon color={colorGrey900} />
                        </IconButton>
                    )}
                </Container>
                {!inTimeline && (
                    <Text size="b_sm" data-testid="title-employment-type">
                        {experience.company.name}{' '}
                        {shouldDisplayEmploymentType(experience) && (
                            <>
                                ·{' '}
                                <I18n
                                    k={`${T_KEY}.employment_type.${
                                        employmentTypeEnumToTranslationKey[experience.employmentType]
                                    }`}
                                />
                            </>
                        )}
                    </Text>
                )}
                <Text size="b_sm" data-testid="work-time" color="bodySecondary">
                    {formatDateRange(experience.startDate, experience.endDate?.valueOf())}
                </Text>
            </Container>
            <Container marginTop="4" hidden={!experience.description}>
                <ExperienceDescription description={experience.description} />
            </Container>
        </Container>
    );
};

function selectSellerSlice(seller: FederatedSeller) {
    return {
        sellerId: seller.user.id,
    };
}

export default ExperienceDetails;
